<template>
  <div class="job-manager-failed-jobs">
    <v-container fluid>
      <!-- Header with actions -->
      <v-row class="mb-4">
        <v-col cols="12" md="6">
          <h1 class="text-h4">{{ $t('job-manager.failed-jobs.title') }}</h1>
        </v-col>
        <v-col cols="12" md="6" class="d-flex justify-end align-center">
          <v-btn
            v-if="handledFilter === '0'"
            small
            outlined
            color="success"
            :disabled="!selectedJobs.length"
            class="mr-2"
            @click="openHandle({jobIds: selectedJobs.map(job => job.id)}, selectedJobs.length)"
          >
            <v-icon left small>mdi-check</v-icon>
            {{ $t('job-manager.failed-jobs.handle-selected') }} ({{ selectedJobs.length }})
          </v-btn>
          <v-btn
            small
            color="primary"
            :disabled="!selectedJobs.length"
            :loading="retryingSelected"
            class="mr-2"
            @click="retrySelected"
          >
            <v-icon left small>mdi-refresh</v-icon>
            {{ $t('job-manager.failed-jobs.retry-selected') }} ({{ selectedJobs.length }})
          </v-btn>
          <v-btn
            small
            color="error"
            :disabled="!selectedJobs.length"
            @click="deleteSelected"
          >
            <v-icon left small>mdi-delete</v-icon>
            {{ $t('job-manager.failed-jobs.delete-selected') }}
          </v-btn>
        </v-col>
      </v-row>

      <!-- To handle / handled: a failure stays « to handle » (and in the daily digest) until someone marks it -->
      <v-row class="mb-2">
        <v-col cols="12" class="d-flex align-center flex-wrap" style="gap: 12px">
          <v-btn-toggle v-model="handledFilter" mandatory dense color="primary" @change="onHandledChange">
            <v-btn small value="0">{{ $t('job-manager.failed-jobs.to-handle') }}<span v-if="unhandled !== null" class="ml-1">({{ unhandled }})</span></v-btn>
            <v-btn small value="1">{{ $t('job-manager.failed-jobs.handled') }}</v-btn>
            <v-btn small value="all">{{ $t('job-manager.failed-jobs.all') }}</v-btn>
          </v-btn-toggle>
          <span class="text-caption text--secondary">{{ $t('job-manager.failed-jobs.handled-hint') }}</span>
        </v-col>
      </v-row>

      <!-- The failures to handle, grouped by job and error -->
      <v-row v-if="handledFilter === '0' && groups.length">
        <v-col cols="12">
          <v-card outlined class="mb-4">
            <v-card-title class="text-subtitle-1">{{ $t('job-manager.failed-jobs.groups-title') }}</v-card-title>
            <v-simple-table dense>
              <tbody>
                <tr v-for="group in groups" :key="group.definitionName + '|' + group.error">
                  <td class="font-weight-medium text-no-wrap">{{ group.definitionName || '?' }}</td>
                  <td class="error--text" style="max-width: 520px">
                    <div class="text-truncate" :title="group.error">{{ group.error || $t('job-manager.failed-jobs.no-error-message') }}</div>
                  </td>
                  <td class="text-no-wrap">
                    {{ $tc('job-manager.failed-jobs.group-count', group.count, {count: group.count}) }}
                    <div v-if="group.errors && group.errors.length > 1" class="text-caption text--secondary">{{ $t('job-manager.failed-jobs.group-variants', {count: group.errors.length}) }}</div>
                  </td>
                  <td class="text-caption text--secondary text-no-wrap">{{ group.lastAt | formatDate }}</td>
                  <td class="text-right text-no-wrap">
                    <v-btn x-small text @click="showGroup(group)">{{ $t('job-manager.failed-jobs.see') }}</v-btn>
                    <v-btn x-small outlined color="success" @click="openHandle({definitionName: group.definitionName, errors: group.errors}, group.count)">{{ $t('job-manager.failed-jobs.handle') }}</v-btn>
                  </td>
                </tr>
              </tbody>
            </v-simple-table>
          </v-card>
        </v-col>
      </v-row>

      <!-- Filters -->
      <v-row>
        <v-col cols="12">
          <v-card class="mb-4">
            <v-card-text>
              <v-row>
                <v-col cols="12" md="4">
                  <v-autocomplete
                    v-model="filters.definitionId"
                    :items="jobDefinitions"
                    item-text="name"
                    item-value="id"
                    :label="$t('job-manager.filters.definition')"
                    clearable
                    @change="onDefinitionChange"
                  />
                </v-col>
                <v-col cols="12" md="4">
                  <date-picker
                    v-model="filters.dateRange"
                    :label="$t('job-manager.filters.date-range')"
                    prepend-icon="mdi-calendar"
                    range
                    clearable
                    @input="onDateRangeChange"
                  />
                </v-col>
                <v-col cols="12" md="4" class="d-flex align-center">
                  <v-btn small color="primary" @click="loadJobs">
                    <v-icon left small>mdi-magnify</v-icon>
                    {{ $t('search') }}
                  </v-btn>
                  <v-btn small text class="ml-2" @click="resetFilters">
                    <v-icon left small>mdi-filter-off</v-icon>
                    {{ $t('job-manager.reset-filters') }}
                  </v-btn>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- Failed Jobs Table -->
      <v-row>
        <v-col cols="12">
          <v-data-table
            v-model="selectedJobs"
            :headers="headers"
            :items="jobs"
            :loading="loading"
            :server-items-length="totalJobs"
            :options.sync="tableOptions"
            :footer-props="footerProps"
            show-select
            item-key="id"
            class="elevation-1"
          >
            <!-- Type Column -->
            <template v-slot:item.type="{ item }">
              <v-chip
                :color="item.type === 'workflow' ? 'purple' : 'blue'"
                small
                outlined
              >
                {{ item.type }}
              </v-chip>
            </template>

            <!-- Created At Column -->
            <template v-slot:item.createdAt="{ item }">
              {{ item.createdAt | formatDate }}
            </template>

            <!-- Execution Count Column -->
            <template v-slot:item.executionCount="{ item }">
              <v-chip small>{{ item.executionCount || item.attempts || 0 }}</v-chip>
            </template>

            <!-- Definition Name Column -->
            <template v-slot:item.definitionName="{ item }">
              <span class="font-weight-medium">{{ item.definitionName }}</span>
            </template>

            <!-- Last Error Column (truncated) -->
            <template v-slot:item.lastError="{ item }">
              <v-tooltip bottom max-width="500">
                <template v-slot:activator="{ on }">
                  <span
                    v-on="on"
                    class="text-truncate d-inline-block error--text"
                    style="max-width: 300px"
                  >
                    {{ item.lastError || '-' }}
                  </span>
                </template>
                <span style="white-space: pre-wrap;">{{ item.lastError || $t('job-manager.failed-jobs.no-error-message') }}</span>
              </v-tooltip>
            </template>

            <!-- Handled: by whom, when, why -->
            <template v-slot:item.handled="{ item }">
              <template v-if="item.failureHandledAt">
                <div class="text-caption">{{ item.failureHandledBy || '-' }} · {{ item.failureHandledAt | formatDate }}</div>
                <div v-if="item.failureNote" class="text-caption text--secondary">{{ item.failureNote }}</div>
              </template>
              <span v-else class="text-caption orange--text">{{ $t('job-manager.failed-jobs.to-handle') }}</span>
            </template>

            <!-- Actions Column -->
            <template v-slot:item.actions="{ item }">
              <div class="d-flex align-center" style="white-space: nowrap;">
                <v-btn
                  icon
                  small
                  @click="viewJob(item)"
                  :title="$t('job-manager.details')"
                >
                  <v-icon small>mdi-eye</v-icon>
                </v-btn>
                <v-btn
                  icon
                  small
                  color="primary"
                  :loading="item._retrying"
                  @click="retryJob(item)"
                  :title="$t('job-manager.retry')"
                >
                  <v-icon small>mdi-refresh</v-icon>
                </v-btn>
                <v-btn
                  icon
                  small
                  color="error"
                  @click="confirmDelete(item)"
                  :title="$t('delete')"
                >
                  <v-icon small>mdi-delete</v-icon>
                </v-btn>
              </div>
            </template>
          </v-data-table>
        </v-col>
      </v-row>

      <!-- Job Details Modal -->
      <job-details-modal
        v-model="showModal"
        :job="selectedJob"
      />

      <!-- Mark as handled, with a note -->
      <v-dialog v-model="handleDialog.open" max-width="480">
        <v-card v-if="handleDialog.open">
          <v-card-title>{{ $tc('job-manager.failed-jobs.handle-title', handleDialog.count, {count: handleDialog.count}) }}</v-card-title>
          <v-card-text>
            <p class="text-body-2">{{ $t('job-manager.failed-jobs.handle-explained') }}</p>
            <v-textarea v-model="handleDialog.note" :label="$t('job-manager.failed-jobs.handle-note')" rows="2" auto-grow outlined dense autofocus />
          </v-card-text>
          <v-card-actions>
            <v-spacer />
            <v-btn small text @click="handleDialog.open = false">{{ $t('cancel') }}</v-btn>
            <v-btn small color="success" :loading="handleDialog.saving" @click="handle">{{ $t('job-manager.failed-jobs.handle') }}</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!-- Delete Confirmation Dialog -->
      <v-dialog v-model="deleteDialog" max-width="400">
        <v-card>
          <v-card-title class="headline">
            {{ $t('job-manager.failed-jobs.confirm-delete') }}
          </v-card-title>
          <v-card-text>
            {{ $t('job-manager.failed-jobs.confirm-delete-message', { count: jobsToDelete.length }) }}
          </v-card-text>
          <v-card-actions>
            <v-spacer />
            <v-btn text @click="deleteDialog = false">
              {{ $t('cancel') }}
            </v-btn>
            <v-btn
              color="error"
              :loading="deleting"
              @click="executeDelete"
            >
              {{ $t('delete') }}
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </v-container>
  </div>
</template>

<script>
import JobRepository from '@repository/JobRepository'
import JobDetailsModal from '../modal/JobDetailsModal.vue'
import DatePicker from '@blocks/DatePicker.vue'

export default {
  name: 'FailedJobs',

  components: {
    JobDetailsModal,
    DatePicker
  },

  data() {
    return {
      jobs: [],
      selectedJobs: [],
      loading: false,
      totalJobs: 0,
      filters: {
        definitionId: null,
        dateRange: []
      },
      tableOptions: {
        page: 1,
        itemsPerPage: 10,
        sortBy: ['createdAt'],
        sortDesc: [true]
      },
      dateMenu: false,
      deleteDialog: false,
      jobsToDelete: [],
      jobDefinitions: [],
      showModal: false,
      selectedJob: null,
      retryingSelected: false,
      deleting: false,
      // '0': to handle (default), '1': handled, 'all'
      handledFilter: '0',
      groups: [],
      unhandled: null,
      handleDialog: {open: false, payload: null, count: 0, note: '', saving: false},
    }
  },

  computed: {
    headers() {
      return [
        { text: this.$t('job-manager.columns.created'), value: 'createdAt', width: '160px' },
        { text: this.$t('job-manager.columns.definition'), value: 'definitionName' },
        { text: this.$t('job-manager.columns.type'), value: 'type', width: '100px' },
        { text: this.$t('job-manager.columns.executions'), value: 'executionCount', width: '100px' },
        { text: this.$t('job-manager.columns.last-error'), value: 'lastError' },
        ...(this.handledFilter === '0' ? [] : [{ text: this.$t('job-manager.failed-jobs.handled'), value: 'handled', sortable: false }]),
        { text: this.$t('job-manager.actions'), value: 'actions', sortable: false, width: '140px' }
      ]
    },
    footerProps() {
      return {
        'items-per-page-options': [10, 50, 100]
      }
    },
    dateRangeText() {
      if (!this.filters.dateRange || this.filters.dateRange.length === 0) {
        return ''
      }
      return this.filters.dateRange.join(' ~ ')
    }
  },

  watch: {
    tableOptions: {
      deep: true,
      handler() {
        this.loadJobs()
      }
    }
  },

  mounted() {
    this.loadJobDefinitions()
    this.loadGroups()
  },

  methods: {
    async loadJobs() {
      this.loading = true
      try {
        const { page, itemsPerPage, sortBy, sortDesc } = this.tableOptions
        const params = {
          limit: itemsPerPage,
          offset: (page - 1) * itemsPerPage,
          orderBy: sortBy && sortBy[0] ? sortBy[0] : 'createdAt',
          orderDirection: sortDesc && sortDesc[0] ? 'desc' : 'asc'
        }

        if (this.filters.definitionId) {
          params.definitionId = this.filters.definitionId
        }
        params.handled = this.handledFilter

        if (this.filters.dateRange && this.filters.dateRange.length === 2) {
          params.dateFrom = this.filters.dateRange[0]
          params.dateTo = this.filters.dateRange[1]
        }

        const result = await JobRepository.failedJobs(params)
        this.jobs = (result.jobs || result.items || []).map(job => ({
          ...job,
          definitionName: job.definitionName || (job.jobDefinition && job.jobDefinition.name) || job.definitionId,
          executionCount: job.executionCount || job.attempts || 0,
          lastError: job.lastError || this.extractLastError(job)
        }))
        this.totalJobs = result.total || 0
      } catch (error) {
        this.notifyError(error)
      } finally {
        this.loading = false
      }
    },

    loadGroups() {
      JobRepository.failureGroups().then(({groups, unhandled}) => {
        this.groups = groups || []
        this.unhandled = unhandled
      }).catch(error => this.notifyError(error))
    },

    onHandledChange() {
      this.selectedJobs = []
      this.tableOptions = {...this.tableOptions, page: 1}
    },

    // A group's jobs: the table filtered on its job definition.
    showGroup(group) {
      const definition = this.jobDefinitions.find(def => def.name === group.definitionName)
      this.filters.definitionId = definition ? definition.id : null
      this.tableOptions = {...this.tableOptions, page: 1}
    },

    openHandle(payload, count) {
      this.handleDialog = {open: true, payload, count, note: '', saving: false}
    },

    async handle() {
      this.handleDialog.saving = true
      try {
        const {handled} = await JobRepository.handleFailures({...this.handleDialog.payload, note: this.handleDialog.note.trim() || null})
        this.notifySuccess('job-manager.failed-jobs.handled-success', {count: handled})
        this.handleDialog.open = false
        this.selectedJobs = []
        this.loadGroups()
        this.loadJobs()
      } catch (error) {
        this.notifyError(error)
        this.handleDialog.saving = false
      }
    },

    extractLastError(job) {
      if (job.executions && job.executions.length > 0) {
        const lastExec = job.executions[job.executions.length - 1]
        return lastExec.error || lastExec.message || null
      }
      return null
    },

    onDefinitionChange() {
      this.loadJobs()
    },

    async loadJobDefinitions() {
      try {
        const result = await JobRepository.jobDefinitions()
        // Handle different response formats
        if (Array.isArray(result)) {
          this.jobDefinitions = result
        } else if (result && Array.isArray(result.items)) {
          this.jobDefinitions = result.items
        } else if (result && Array.isArray(result.definitions)) {
          this.jobDefinitions = result.definitions
        } else {
          this.jobDefinitions = []
        }
      } catch (error) {
        console.error('Failed to load job definitions', error)
        this.jobDefinitions = []
      }
    },

    async viewJob(job) {
      // Fetch full job data to get executions, jobDefinition, etc.
      try {
        const fullJob = await JobRepository.getJob(job.id)
        this.selectedJob = fullJob
      } catch (error) {
        // Fallback to basic job data if fetch fails
        console.warn('Could not fetch full job data:', error)
        this.selectedJob = this.cloneObj(job)
      }
      this.showModal = true
    },

    async retryJob(job) {
      this.$set(job, '_retrying', true)
      try {
        await JobRepository.retryJob(job.id)
        this.notifySuccess('job-manager.failed-jobs.retry-success')
        this.loadJobs()
      } catch (error) {
        this.notifyError(error)
      } finally {
        this.$set(job, '_retrying', false)
      }
    },

    async retrySelected() {
      this.retryingSelected = true
      try {
        const promises = this.selectedJobs.map(job => JobRepository.retryJob(job.id))
        await Promise.all(promises)
        this.notifySuccess('job-manager.failed-jobs.retry-selected-success', { count: this.selectedJobs.length })
        this.selectedJobs = []
        this.loadJobs()
      } catch (error) {
        this.notifyError(error)
      } finally {
        this.retryingSelected = false
      }
    },

    confirmDelete(job) {
      this.jobsToDelete = [job]
      this.deleteDialog = true
    },

    deleteSelected() {
      this.jobsToDelete = [...this.selectedJobs]
      this.deleteDialog = true
    },

    async executeDelete() {
      this.deleting = true
      try {
        const promises = this.jobsToDelete.map(job => JobRepository.deleteJob(job.id))
        await Promise.all(promises)
        this.notifySuccess('job-manager.failed-jobs.delete-success', { count: this.jobsToDelete.length })
        this.selectedJobs = []
        this.jobsToDelete = []
        this.deleteDialog = false
        this.loadJobs()
      } catch (error) {
        this.notifyError(error)
      } finally {
        this.deleting = false
      }
    },

    onDateRangeChange() {
      if (this.filters.dateRange && this.filters.dateRange.length === 2) {
        this.dateMenu = false
        this.loadJobs()
      }
    },

    clearDateRange() {
      this.filters.dateRange = []
      this.loadJobs()
    },

    resetFilters() {
      this.filters = {
        definitionId: null,
        dateRange: []
      }
      this.loadJobs()
    }
  }
}
</script>

<style lang="scss" scoped>
.job-manager-failed-jobs {
  padding: 20px;
}
</style>
